/**
 * Agent URL and gateway token configuration, keyed per paradigm user.
 *
 * Each paradigm user gets their own OpenClaw pairing, so tokens, gateway
 * URLs, and custom apps are namespaced by user id.
 *
 * @note Target audience: Paradigm/Paradex traders. Communication should be
 * concise and information-dense unless specifically asked for explanation.
 */

import { hmac } from '@noble/hashes/hmac';
import { sha256 } from '@noble/hashes/sha256';
import { bytesToHex } from '@noble/hashes/utils';

declare const AI_AGENT_URL: string | undefined;
declare const FE_DOMAIN: string | undefined;
declare const API_URL: string | undefined;
declare const DIME_TERMINAL_SLUG_SECRET: string | undefined;

const tokenKey = (userId: string) =>
  `${userId.toLowerCase()}:paradigm_ai_agent_token`;
const deviceTokenKey = (userId: string) =>
  `${userId.toLowerCase()}:paradigm_ai_agent_device_token`;
const urlKey = (userId: string) =>
  `${userId.toLowerCase()}:paradigm_ai_agent_url`;
const selectedModelKey = (userId: string) =>
  `${userId.toLowerCase()}:paradigm_terminal_selected_model`;

// Legacy `<suffix>:<userId>` format. Read once and migrate to the new
// `<userId>:<suffix>` layout that matches the rest of the Paradigm UI.
const legacyTokenKey = (userId: string) =>
  `paradigm_ai_agent_token:${userId.toLowerCase()}`;
const legacyDeviceTokenKey = (userId: string) =>
  `paradigm_ai_agent_device_token:${userId.toLowerCase()}`;
const legacyUrlKey = (userId: string) =>
  `paradigm_ai_agent_url:${userId.toLowerCase()}`;

function readWithMigration(newKey: string, legacyKey: string): string | null {
  const current = localStorage.getItem(newKey);
  if (current != null) return current;
  const legacy = localStorage.getItem(legacyKey);
  if (legacy == null) return null;
  try {
    localStorage.setItem(newKey, legacy);
    localStorage.removeItem(legacyKey);
  } catch {
    // best-effort migration
  }
  return legacy;
}

// --- Default URL Derivation ---

function slugSuffix(userId: number): string {
  const secret =
    typeof DIME_TERMINAL_SLUG_SECRET !== 'undefined' &&
    DIME_TERMINAL_SLUG_SECRET.length > 0
      ? DIME_TERMINAL_SLUG_SECRET
      : null;
  if (secret == null) return String(userId);
  return bytesToHex(hmac(sha256, secret, String(userId))).slice(0, 8);
}

const KNOWN_DOMAINS = [
  'paradigm.trade',
  'paradex.trade',
  'terminal.paradigm.tech',
];

function detectBaseDomain(): string | null {
  for (const candidate of [FE_DOMAIN, API_URL]) {
    try {
      if (typeof candidate === 'undefined' || candidate.length === 0) continue;
      const { hostname } = new URL(candidate);
      const match = KNOWN_DOMAINS.find(
        (d) => hostname.endsWith(`.${d}`) || hostname === d,
      );
      if (match != null) return match;
    } catch {
      // ignore unparseable URLs
    }
  }
  return null;
}

/**
 * Derive a default agent URL from ticker and user ID.
 * e.g. ticker="aqtl", userId=238
 *   → "wss://aqtl-6b13722d.terminal.paradigm.tech"
 */
export function deriveDefaultAgentUrl(ticker: string, userId: number): string {
  const baseDomain = detectBaseDomain();
  if (baseDomain == null) return '';
  return `wss://${ticker.toLowerCase()}-${slugSuffix(
    userId,
  )}.terminal.paradigm.tech`;
}

// --- URL ---

export function getAgentUrl(userId: string): string | null {
  try {
    const stored = readWithMigration(urlKey(userId), legacyUrlKey(userId));
    if (stored != null && stored.length > 0) return stored;
  } catch {
    // localStorage unavailable
  }

  if (typeof AI_AGENT_URL !== 'undefined' && AI_AGENT_URL.length > 0) {
    return AI_AGENT_URL;
  }

  return null;
}

export function setAgentUrl(userId: string, url: string): void {
  try {
    if (url.length === 0) {
      localStorage.removeItem(urlKey(userId));
    } else {
      localStorage.setItem(urlKey(userId), url);
    }
    localStorage.removeItem(legacyUrlKey(userId));
  } catch {
    // localStorage unavailable
  }
}

export function clearAgentUrl(userId: string): void {
  try {
    localStorage.removeItem(urlKey(userId));
    localStorage.removeItem(legacyUrlKey(userId));
  } catch {
    // localStorage unavailable
  }
}

// --- Gateway Token ---

export function getAgentToken(userId: string): string | null {
  try {
    const stored = readWithMigration(tokenKey(userId), legacyTokenKey(userId));
    if (stored != null && stored.length > 0) return stored;
  } catch {
    // localStorage unavailable
  }
  return null;
}

export function setAgentToken(userId: string, token: string): void {
  try {
    if (token.length === 0) {
      localStorage.removeItem(tokenKey(userId));
    } else {
      localStorage.setItem(tokenKey(userId), token);
    }
    localStorage.removeItem(legacyTokenKey(userId));
  } catch {
    // localStorage unavailable
  }
}

export function clearAgentToken(userId: string): void {
  try {
    localStorage.removeItem(tokenKey(userId));
    localStorage.removeItem(legacyTokenKey(userId));
  } catch {
    // localStorage unavailable
  }
}

// --- Device Token ---
//
// Returned by the gateway in `hello-ok.auth.deviceToken` after a successful
// connect. Reconnecting with this token preserves the originally approved
// scopes instead of collapsing to the admin-only default. Prefer it over the
// pairing token on subsequent connects.

export function getDeviceToken(userId: string): string | null {
  try {
    const stored = readWithMigration(
      deviceTokenKey(userId),
      legacyDeviceTokenKey(userId),
    );
    if (stored != null && stored.length > 0) return stored;
  } catch {
    // localStorage unavailable
  }
  return null;
}

export function setDeviceToken(userId: string, token: string): void {
  try {
    if (token.length === 0) {
      localStorage.removeItem(deviceTokenKey(userId));
    } else {
      localStorage.setItem(deviceTokenKey(userId), token);
    }
    localStorage.removeItem(legacyDeviceTokenKey(userId));
  } catch {
    // localStorage unavailable
  }
}

export function clearDeviceToken(userId: string): void {
  try {
    localStorage.removeItem(deviceTokenKey(userId));
    localStorage.removeItem(legacyDeviceTokenKey(userId));
  } catch {
    // localStorage unavailable
  }
}

// --- Session Reset ---

/**
 * Clear both the pairing token and the device token for a user.
 *
 * Tokens are keyed by user id only (not by URL), so without an explicit
 * reset the device token from a prior OpenClaw instance persists in
 * localStorage and gets replayed against the new instance on reconnect —
 * which fails with "gateway token mismatch".
 *
 * Intentionally does NOT clear the URL: callers decide whether to
 * rewrite (setup flow) or remove (disconnect flow) it.
 */
export function clearAgentSession(userId: string): void {
  clearAgentToken(userId);
  clearDeviceToken(userId);
}

// --- Selected Model ---

export function getSelectedModel(userId: string): string | null {
  try {
    const stored = localStorage.getItem(selectedModelKey(userId));
    if (stored != null && stored.length > 0) return stored;
  } catch {
    // localStorage unavailable
  }
  return null;
}

export function setSelectedModel(userId: string, modelId: string): void {
  try {
    if (modelId.length === 0) {
      localStorage.removeItem(selectedModelKey(userId));
    } else {
      localStorage.setItem(selectedModelKey(userId), modelId);
    }
  } catch {
    // localStorage unavailable
  }
}

// --- Dashboard URL Derivation ---

/**
 * Derive the HTTPS dashboard URL from a WebSocket agent URL.
 * ws://host:port  → https://host/overview
 * wss://host:port → https://host/overview
 */
export function deriveAgentDashboardUrl(agentWsUrl: string): string | null {
  try {
    const parsed = new URL(agentWsUrl);
    return `https://${parsed.hostname}/overview`;
  } catch {
    return null;
  }
}

// --- Validation ---

/**
 * Validate that a URL is a usable OpenClaw gateway WebSocket URL: must be
 * `ws://` or `wss://` and must include a host. Mirrors paradex's
 * `ValidateAiAgentUrl` (api/paradex/web-api/requests/account.go).
 */
export function isValidAgentUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'ws:' && parsed.protocol !== 'wss:') return false;
    if (parsed.hostname.length === 0) return false;
    return true;
  } catch {
    return false;
  }
}
